import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { ThreeDotMenuButton } from '@/styles/StandardButtons';
import { useProject } from '@/components/ProjectSelector';
import { manualComparisonService } from '../services/manual-comparison-service';
import type { ManualComparisonSummary } from '../types';

const formatDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString() : '—');

/** The company's saved Comparison Sheets (of the selected project, or of all): click one to keep working on it. */
export function SavedComparisonSheets({ onOpenSheet }: { onOpenSheet: (sheetId: string) => void }) {
  const { selectedProject } = useProject();
  const [sheets, setSheets] = useState<ManualComparisonSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setSheets(await manualComparisonService.getComparisons(selectedProject !== 'all' ? selectedProject : undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the Comparison Sheets');
    } finally {
      setIsLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteSelected = async () => {
    if (selected.length === 0) {
      window.alert('Please select at least one Comparison Sheet to delete.');
      return;
    }
    if (!window.confirm(`Delete ${selected.length} Comparison Sheet(s)? This action cannot be undone.`)) return;
    const results = await Promise.allSettled(selected.map((id) => manualComparisonService.deleteComparison(id)));
    const failed = results.filter((result) => result.status === 'rejected').length;
    if (failed > 0) setError(`${failed} Comparison Sheet(s) could not be deleted.`);
    setSelected([]);
    load();
  };

  const columns = useMemo<GenericTableColumn<ManualComparisonSummary>[]>(() => [
    { key: 'name', header: 'Name', width: '16rem', sortable: true, render: (row) => <span className="font-medium">{row.name}</span> },
    { key: 'project_name', header: 'Project', width: '14rem', sortable: true },
    { key: 'columnCount', header: 'Vendors', width: '6rem', sortable: true, render: (row) => <span className="text-center block">{row.columnCount}</span> },
    { key: 'itemCount', header: 'Items', width: '6rem', sortable: true, render: (row) => <span className="text-center block">{row.itemCount}</span> },
    { key: 'created_by_name', header: 'Created By', width: '10rem', sortable: true },
    { key: 'created_at', header: 'Created', width: '8rem', sortable: true, render: (row) => formatDate(row.created_at) },
  ], []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <ThreeDotMenuButton items={[{ label: 'Delete', icon: <Trash2 className="w-4 h-4" />, onClick: deleteSelected, destructive: true }]} />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <GenericTable
        columns={columns}
        data={sheets}
        loading={isLoading}
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        rowKey={(row) => row.id}
        onRowClick={(row) => onOpenSheet(row.id)}
        defaultSort={{ columnKey: 'created_at', direction: 'desc' }}
        minTableWidth={900}
        emptyState={
          <div className="py-10 text-center text-gray-500">
            <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="font-medium text-gray-700">No saved Comparison Sheets{selectedProject !== 'all' ? ' for this project' : ''}</p>
            <p className="text-sm">Build one in the Comparison Sheet tab and press Save.</p>
          </div>
        }
      />
    </div>
  );
}
